/* eslint-disable prefer-const */
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import { EndpointsMvp } from '../MVP/enum';
import {
  FAQProps,
  FAQSources,
  HelpCenterCategory,
  HelpCenterListResponse,
  HelpCenterTopic,
  HitsSource,
  OpenSearchResponse
} from '../../store/ducks/help-center/types';
import { ISearchResult } from '@gerdau/hefesto/SearchField/SearchField.types';

export class HelpCenter extends EGerdauService {
  async getContent(keywordsNotAllowed: string[]): Promise<FAQProps> {
    const headers = super.getHeaders();
    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.HELPCENTER}`,
      {
        query: {
          match_all: {}
        },
        size: 100
      },
      { headers }
    );

    const hits: HitsSource[] = response?.data?.hits?.hits ?? [];

    const convertHits: FAQSources[] =
      hits?.map((item: HitsSource) => item._source) || [];

    const data: FAQProps = convertHits.reduce<FAQProps>(
      (acc, item) => {
        const notAllowed = item.keywords.some((itm) =>
          keywordsNotAllowed.includes(itm)
        );

        if (!notAllowed) {
          if (item.type === 'category') {
            return {
              ...acc,
              categories: [...acc.categories, item]
            };
          }

          if (item.type === 'question') {
            return {
              ...acc,
              question: [...acc.question, item]
            };
          }
        }
        return {
          ...acc
        };
      },
      {
        categories: [],
        question: [],
        query: {
          searchTerm: '',
          results: []
        },
        suggestions: []
      }
    );

    return data || {};
  }

  async getContentByQuery({
    faq,
    keywordsNotAllowed
  }: {
    faq: FAQProps;
    keywordsNotAllowed: string[];
  }): Promise<FAQProps> {
    const headers = super.getHeaders();

    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.HELPCENTER}`,
      {
        query: {
          bool: {
            must: [
              {
                match: {
                  question: {
                    query: faq.query.searchTerm
                  }
                }
              }
            ],
            filter: [
              {
                term: {
                  type: 'question'
                }
              }
            ]
          }
        },
        collapse: {
          field: 'question.keyword'
        },
        sort: [
          {
            'question.keyword': {
              order: 'asc'
            }
          }
        ],
        size: 100
      },
      { headers }
    );

    const hits: HitsSource[] = response?.data?.hits?.hits ?? [];

    const convertHits: FAQSources[] =
      hits?.map((item: HitsSource) => item._source) || [];

    const data: FAQProps = convertHits.reduce<FAQProps>(
      (acc, item) => {
        const notAllowed = item.keywords.some((itm) =>
          keywordsNotAllowed.includes(itm)
        );
        if (!notAllowed) {
          if (item.type === 'question') {
            return {
              ...acc,
              query: {
                ...acc.query,
                results: [...acc.query.results, item]
              }
            };
          }
        }

        return {
          ...acc
        };
      },
      {
        categories: [],
        question: [],
        query: {
          searchTerm: '',
          results: []
        },
        suggestions: []
      }
    );

    return data || {};
  }

  async getContentSugestion({
    query
  }: {
    query: string;
  }): Promise<ISearchResult[]> {
    const headers = super.getHeaders();

    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.HELPCENTER}`,
      {
        suggest: {
          did_you_mean: {
            text: query,
            term: {
              field: 'question.suggestion',
              suggest_mode: 'always',
              min_word_length: 3
            }
          }
        },
        size: 100
      },
      { headers }
    );

    const data: OpenSearchResponse = response?.data;

    const suggestions: ISearchResult[] | [] =
      data.suggest?.did_you_mean?.map((item) => ({
        id: item.options[0].text,
        label: item.options[0].text
      })) ?? [];

    return suggestions;
  }

  async getCategories(): Promise<HelpCenterCategory[]> {
    const headers = super.getHeaders({ withCommerceToken: false });

    const response = await customersAuthorizationApi.get<
      HelpCenterListResponse<HelpCenterCategory>
    >(EndpointsMvp.HELP_CENTER_CATEGORIES, {
      headers,
      params: {
        'searchCriteria[pageSize]': 50,
        'searchCriteria[currentPage]': 1
      }
    });

    return response?.data?.items ?? [];
  }

  async getTopics(categoryId: number): Promise<HelpCenterTopic[]> {
    const headers = super.getHeaders({ withCommerceToken: false });

    const response = await customersAuthorizationApi.get<
      HelpCenterListResponse<HelpCenterTopic>
    >(EndpointsMvp.HELP_CENTER_TOPICS, {
      headers,
      params: {
        'searchCriteria[pageSize]': 50,
        'searchCriteria[currentPage]': 1,
        'searchCriteria[sortOrders][0][field]': 'sort_order',
        'searchCriteria[sortOrders][0][direction]': 'asc',
        'searchCriteria[filterGroups][0][filters][0][field]': 'category_id',
        'searchCriteria[filterGroups][0][filters][0][value]': categoryId,
        'searchCriteria[filterGroups][0][filters][0][condition_type]': 'eq'
      }
    });

    return response?.data?.items ?? [];
  }

  async searchTopics(searchInput: string): Promise<HelpCenterTopic[]> {
    const headers = super.getHeaders({ withCommerceToken: false });

    const response = await customersAuthorizationApi.get<
      HelpCenterListResponse<HelpCenterTopic>
    >(EndpointsMvp.HELP_CENTER_TOPICS, {
      headers,
      params: {
        'searchCriteria[pageSize]': 50,
        'searchCriteria[currentPage]': 1,
        'searchCriteria[sortOrders][0][field]': 'sort_order',
        'searchCriteria[sortOrders][0][direction]': 'asc',
        'searchCriteria[filterGroups][0][filters][0][field]': 'meta_keywords',
        'searchCriteria[filterGroups][0][filters][0][value]': `%${searchInput}%`,
        'searchCriteria[filterGroups][0][filters][0][condition_type]': 'like',
        'searchCriteria[filterGroups][0][filters][1][field]': 'description',
        'searchCriteria[filterGroups][0][filters][1][value]': `%${searchInput}%`,
        'searchCriteria[filterGroups][0][filters][1][condition_type]': 'like'
      }
    });

    return response?.data?.items ?? [];
  }
}
